import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Dialog from "@material-ui/core/Dialog";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
	"@keyframes ttIn": {
		from: { opacity: 0, transform: "translateY(10px) scale(.97)" },
		to: { opacity: 1, transform: "translateY(0) scale(1)" },
	},
	"@keyframes ttSpin": {
		to: { transform: "rotate(360deg)" },
	},
	paper: {
		borderRadius: 22,
		width: 620,
		maxWidth: "calc(100vw - 32px)",
		background: theme.palette.background.paper,
	},
	header: {
		display: "flex",
		alignItems: "center",
		padding: "20px 22px 8px",
	},
	titleBox: { flex: 1 },
	title: {
		fontWeight: 800,
		fontSize: "1.25rem",
		letterSpacing: "-0.02em",
	},
	subtitle: {
		fontSize: "0.85rem",
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
	body: {
		padding: "8px 22px 24px",
		maxHeight: "70vh",
		overflowY: "auto",
		...theme.scrollbarStyles,
	},
	sectionLabel: {
		fontSize: "0.68rem",
		fontWeight: 800,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: theme.palette.text.secondary,
		margin: "14px 2px 10px",
	},
	grid: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
		gap: 12,
	},
	card: {
		position: "relative",
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "12px 14px",
		borderRadius: 14,
		border: `1px solid ${theme.custom.border}`,
		backgroundColor: theme.custom.surface,
		cursor: "pointer",
		userSelect: "none",
		animation: "$ttIn .35s cubic-bezier(.2,.9,.3,1.1) both",
		transition:
			"transform .18s cubic-bezier(.2,.9,.3,1.2), box-shadow .18s ease, border-color .18s ease",
		"&:hover": {
			transform: "translateY(-3px)",
			borderColor: "#10B981",
			boxShadow:
				theme.palette.type === "dark"
					? "0 12px 28px rgba(0,0,0,.5)"
					: "0 12px 28px rgba(16,185,129,.22)",
		},
		"&:active": { transform: "translateY(-1px) scale(.99)" },
	},
	cardBusy: {
		pointerEvents: "none",
		borderColor: "#10B981",
		boxShadow: "0 0 0 3px rgba(16,185,129,.25)",
	},
	avatar: {
		width: 42,
		height: 42,
		fontWeight: 800,
		fontSize: "0.95rem",
		backgroundColor: theme.palette.primary.main,
		color: "#fff",
	},
	queueAvatar: {
		width: 42,
		height: 42,
		backgroundColor: "rgba(245,158,11,.15)",
		color: "#F59E0B",
	},
	cardName: {
		fontWeight: 700,
		fontSize: "0.9rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	cardMeta: {
		fontSize: "0.74rem",
		color: theme.palette.text.secondary,
	},
	youChip: {
		fontSize: "0.62rem",
		fontWeight: 800,
		color: "#10B981",
		border: "1px solid rgba(16,185,129,.5)",
		borderRadius: 999,
		padding: "1px 7px",
		marginLeft: 6,
	},
	spinner: {
		position: "absolute",
		right: 12,
		color: "#10B981",
	},
	empty: {
		textAlign: "center",
		color: theme.palette.text.secondary,
		padding: "30px 0",
		fontSize: "0.9rem",
	},
	loadingWrap: {
		display: "flex",
		justifyContent: "center",
		padding: "40px 0",
	},
}));

const initials = (name) =>
	(name || "?")
		.split(" ")
		.map((n) => n[0])
		.slice(0, 2)
		.join("")
		.toUpperCase();

const TransferTicketModal = ({ modalOpen, onClose, ticketid }) => {
	const classes = useStyles();
	const history = useHistory();
	const { user: loggedInUser } = useContext(AuthContext);

	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [busyKey, setBusyKey] = useState(null);

	useEffect(() => {
		if (!modalOpen) return;
		let active = true;
		setLoading(true);
		(async () => {
			try {
				const { data } = await api.get("/tickets/team-board", {
					params: { queueIds: JSON.stringify([]) },
				});
				if (active) {
					setUsers(data.users || []);
					setLoading(false);
				}
			} catch (err) {
				if (active) setLoading(false);
				toastError(err);
			}
		})();
		return () => {
			active = false;
		};
	}, [modalOpen]);

	const transfer = async (key, payload) => {
		if (!ticketid || busyKey) return;
		setBusyKey(key);
		try {
			await api.put(`/tickets/${ticketid}`, payload);
			onClose();
			setBusyKey(null);
			history.push("/tickets");
		} catch (err) {
			setBusyKey(null);
			toastError(err);
		}
	};

	const toUser = (u) =>
		transfer(`u${u.id}`, { status: "open", userId: u.id });

	const toQueue = () =>
		transfer("pending", { status: "pending", userId: null });

	const orderedUsers = [...users].sort((a, b) => {
		if (a.id === loggedInUser?.id) return -1;
		if (b.id === loggedInUser?.id) return 1;
		return a.name.localeCompare(b.name);
	});

	return (
		<Dialog
			open={modalOpen}
			onClose={onClose}
			classes={{ paper: classes.paper }}
			maxWidth={false}
		>
			<div className={classes.header}>
				<div className={classes.titleBox}>
					<Typography className={classes.title}>
						Transferir atendimento
					</Typography>
					<Typography className={classes.subtitle}>
						Toque em quem vai receber — a transferência é na hora.
					</Typography>
				</div>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</div>

			<div className={classes.body}>
				{loading ? (
					<div className={classes.loadingWrap}>
						<CircularProgress size={30} />
					</div>
				) : (
					<>
						<Typography className={classes.sectionLabel}>
							Devolver para a fila
						</Typography>
						<div className={classes.grid}>
							<div
								className={`${classes.card} ${
									busyKey === "pending" ? classes.cardBusy : ""
								}`}
								onClick={toQueue}
							>
								<Avatar className={classes.queueAvatar}>
									<HourglassEmptyIcon />
								</Avatar>
								<div style={{ minWidth: 0 }}>
									<div className={classes.cardName}>Aguardando</div>
									<div className={classes.cardMeta}>Volta para a fila</div>
								</div>
								{busyKey === "pending" && (
									<CircularProgress size={18} className={classes.spinner} />
								)}
							</div>
						</div>

						<Typography className={classes.sectionLabel}>
							Atendentes
						</Typography>
						{orderedUsers.length === 0 ? (
							<div className={classes.empty}>
								Nenhum atendente disponível.
							</div>
						) : (
							<div className={classes.grid}>
								{orderedUsers.map((u) => {
									const isMe = u.id === loggedInUser?.id;
									const busy = busyKey === `u${u.id}`;
									return (
										<div
											key={u.id}
											className={`${classes.card} ${
												busy ? classes.cardBusy : ""
											}`}
											onClick={() => toUser(u)}
										>
											<Avatar className={classes.avatar}>
												{initials(u.name)}
											</Avatar>
											<div style={{ minWidth: 0, flex: 1 }}>
												<div className={classes.cardName}>
													{u.name}
													{isMe && <span className={classes.youChip}>VOCÊ</span>}
												</div>
												<div className={classes.cardMeta}>
													{u.profile === "admin" ? "Administrador" : "Atendente"}
												</div>
											</div>
											{busy ? (
												<CircularProgress
													size={18}
													className={classes.spinner}
												/>
											) : (
												<CheckCircleIcon
													style={{
														color: "rgba(148,163,184,.4)",
														fontSize: 20,
													}}
												/>
											)}
										</div>
									);
								})}
							</div>
						)}
					</>
				)}
			</div>
		</Dialog>
	);
};

export default TransferTicketModal;
